import { useState, useEffect } from "react";
import { colors, css, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { AicoConnection, NewSelect, PageHeader, AreaLayout, WaitAnimation, NewButtonList, NewButton, TableTypes, LoadingSpinner } from "../../../../../Components";
import { ButtonTypes, NewFieldStyles } from "../../../../../Definitions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { AVAILABLE_FIRMWARE_LC, AvailableFirmwareLCArgs, AvailableFirmwareLCResponse, FIRMWARE_UPDATE_ABORT, FIRMWARESYSTEM_INFO, FirmwareUpdateAbortArgs, FirmwareUpdateAbortResponse, LCFirmwareArgs, LCFirmwareResponse, UPDATEFIRMWARE_SYSTEM, UpdateFirmwareArgs, UpdateFirmwareResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/firmwareCalls";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

export default function Firmware() {
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 
	const [firmwareFileName, setFirmwareFileName] = useState<string | null>(null);

	const { state: connectionState } = useAicoConnection();

	const [fetchAvailable, { data: dataAvailable, loading: loadingAvailable, error: errorAvailable }] = useLazyQuery<AvailableFirmwareLCResponse, AvailableFirmwareLCArgs>(
		AVAILABLE_FIRMWARE_LC,
		{ fetchPolicy: "network-only" }
	);

	const [fetchCurrent, { data: dataCurrent, loading: loadingCurrent, error: errorCurrent }] = useLazyQuery<LCFirmwareResponse, LCFirmwareArgs>(
		FIRMWARESYSTEM_INFO,
		{ fetchPolicy: "network-only" }
	);

	const [updateFirmware, { data: dataUpdate, loading: loadingUpdate, error: errorUpdate }] = useMutation<UpdateFirmwareResponse, UpdateFirmwareArgs>(
		UPDATEFIRMWARE_SYSTEM,
		{ fetchPolicy: "network-only" }
	);

	const [firmwareUpdateAbort, { data: dataAbort, loading: loadingAbort, error: errorAbort }] = useMutation<FirmwareUpdateAbortResponse, FirmwareUpdateAbortArgs>(
		FIRMWARE_UPDATE_ABORT,
		{ fetchPolicy: "network-only" }
	);

	useEffect(() => {
		if (connectionState.domain) {
			fetchAvailable({ variables: { domain: connectionState.domain } });
		} else {
			setFirmwareFileName(null);
		}

		if (connectionState.deviceConnection) {
			fetchCurrent({ variables: { ...connectionState.deviceConnection } });
		}
	}, [connectionState]);

	const selectFirmwareFile = firmwareFileName ? { label: firmwareFileName, value: firmwareFileName, id: firmwareFileName } : undefined;

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Software' subHeader='LC Firmware' />
			<frames._page_inner_content>
				<AreaLayout
					tableType={TableTypes.fitContent}
					headerArea={mobileXs ? undefined : <AicoConnection inTable/>}
					buttonArea={<div style={{ display: "flex", gap: "5px" }}>
						{mobileXs && <AicoConnection inTable/>}
						<NewButton
							content={loadingAvailable ? "Fetching..." : "Fetch avail."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchAvailable({ variables: { domain: connectionState.domain } });
								}
							}}
							disabled={loadingAvailable || !connectionState.deviceConnection}
						/>
						<NewButton
							content={loadingCurrent ? "Fetching..." : "Fetch curr."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.deviceConnection) {
									fetchCurrent({ variables: { ...connectionState.deviceConnection } });
								}
							}}
							disabled={loadingCurrent || !connectionState.deviceConnection}
						/>
					</div>}
					contentArea={
						<div style={{ display: "flex", flexDirection: "column", gap: "50px", height: "100%", alignItems: "center" }}>
							<div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap", justifyContent: "center"}}>
								<NewSelect
									header="Update Firmware"
									placeholder="New firmware version"
									onChange={(selectedOption) => {
										if (selectedOption?.value) {
											setFirmwareFileName(selectedOption?.value)
										}
									}}
									options={dataAvailable?.controller.availableFirmware.map((firmwareVersion) => ({ label: firmwareVersion.fileName, value: firmwareVersion.fileName })) ?? []}
									fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
									isRequired={true}
									isSearchable={true}
									valueOverrides={{defaultValue: selectFirmwareFile}}
								/>
								<NewButtonList
									onClick={(i) => {
										if (i === 0) {if (firmwareFileName && connectionState.deviceConnection) {
											updateFirmware({ variables: { ...connectionState.deviceConnection, fileName: firmwareFileName } })
										}}
										if (i === 1) { if (connectionState.deviceConnection) {
											firmwareUpdateAbort({ variables: { ...connectionState.deviceConnection } })
										}}
									}}
									disabled={[loadingUpdate || !firmwareFileName || !connectionState.deviceConnection, loadingAbort || !connectionState.deviceConnection]}
									contents={["Update Firmware", "abort"]}
									overrides={{
										baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
										contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
										hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
									}}
								/>
							</div>

							<div style={{ width: "100%", maxWidth: "500px" }}>
								<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
									Available Firmware Versions
								</div>
								{loadingAvailable && <WaitAnimation/>}
								{errorAvailable && <div style={{ ...css.error, }}>Error</div>}
								{!loadingAvailable && !errorAvailable ? dataAvailable?.controller.availableFirmware.length
									?
									dataAvailable.controller.availableFirmware.map((firmwareVersion, i) => (
										<div key={i} style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
											{firmwareVersion.fileName}
										</div>
									))
									:
									<div style={{ ...css.emptyPen, textAlign: "left", opacity: "0.5" }}>
										No firmware available
									</div>
									:
									<></>
								}
							</div>

							<div style={{ width: "100%", maxWidth: "500px" }}>
								<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
									Current firmware
								</div>
								{loadingCurrent && <WaitAnimation/>}
								{errorCurrent && <div style={{ ...css.error,}}>Error</div>}
								{!loadingCurrent && !errorCurrent ? dataCurrent?.controller.config
									?
									<div style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
										{dataCurrent.controller.config.firmwareVersion}
									</div>
									:
									<div style={{ ...css.emptyPen, textAlign: "left", opacity: "0.5" }}>
										Unknown
									</div>
									:
									<></>
								}
							</div>

							{loadingAbort && <LoadingSpinner/>}
							{(errorAbort || (dataAbort && !dataAbort.controller.firmwareUpdateAbort.success))
								&& <p style={{ ...css.error, maxWidth: "500px", textAlign: "center" }}>Error: Failed to abort firmware update</p>
							}
							{!loadingAbort
								&& !errorAbort
								&& dataAbort?.controller.firmwareUpdateAbort.success
								&& <p style={{ ...css.success, maxWidth: "500px", textAlign: "center" }}>Succesfully aborted firmware update</p>
							}

							{loadingUpdate && <LoadingSpinner/>}
							{(errorUpdate || (dataUpdate && !dataUpdate.controller.firmwareUpdate.success))
								&& <p style={{ ...css.error, maxWidth: "500px", textAlign: "center" }}>Error: Failed to update firmware</p>
							}
							{!loadingUpdate
								&& !errorUpdate
								&& dataUpdate?.controller.firmwareUpdate.success
								&& <p style={{ ...css.success, maxWidth: "500px", textAlign: "center" }}>Succesfully updated firmware</p>
							}

						</div>
					}
				/>

			</frames._page_inner_content>
		</frames.Page_inner>
	);
}